// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
/**
 * Single tab stop navigation context is used together with keyboard navigation that requires a single tab stop.
 * It instructs interactive elements to override tab indices for just a single one to remain user-focusable.
 */
export const SingleTabStopNavigationContext = createContext({
    navigationActive: false,
    registerFocusable: () => () => { },
});
export function useSingleTabStopNavigation(focusable, options) {
    var _a;
    const { navigationActive: contextNavigationActive, registerFocusable: contextRegisterFocusable } = useContext(SingleTabStopNavigationContext);
    const [focusTargetActive, setFocusTargetActive] = useState(false);
    const navigationActive = contextNavigationActive && (!(options === null || options === void 0 ? void 0 : options.tabIndex) || (options === null || options === void 0 ? void 0 : options.tabIndex) >= 0);
    const registerFocusable = useCallback((focusable, changeHandler) => navigationActive ? contextRegisterFocusable(focusable, changeHandler) : () => { }, [navigationActive, contextRegisterFocusable]);
    useEffect(() => {
        if (focusable) {
            const changeHandler = (element, suppressed) => setFocusTargetActive(focusable.current === element || suppressed);
            const unregister = registerFocusable(focusable, changeHandler);
            return () => unregister();
        }
    }, [focusable, registerFocusable]);
    let tabIndex = options === null || options === void 0 ? void 0 : options.tabIndex;
    if (navigationActive) {
        tabIndex = !focusTargetActive ? -1 : (_a = options === null || options === void 0 ? void 0 : options.tabIndex) !== null && _a !== void 0 ? _a : 0;
    }
    return { navigationActive, tabIndex };
}
//# sourceMappingURL=single-tab-stop-navigation-context.js.map